import React, { useEffect, useState } from 'react';
import {
  FileText,
  Download,
  Calendar,
  Package,
  FileBarChart,
  Loader2,
  ChevronDown,
  FileCheck,
  PlayCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Select, Row, Col, Card, Space, Form, Typography, Tag } from 'antd';
import StatCard from '@/components/StatCard';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  bp_get_batch_number_by_recipe,
  bp_get_batch_report_view,
  get_new_pidilite_pdf_report,
  get_recipe_by_product_id,
  get_batch_report_pdf,
  bp_product_data,
  get_product_list_both,
  get_batch_report_pdf_for_multiple_lots,
} from '@/utils/api';
import { base64ToFile } from '@/pages/reusable_functions';
import ReportSkeleton from './ReportSkeleton';
import DetailedReport from './reports/DetailedReport';
import RunningReport from './reports/RunningReport';
import ProductionRecordReport from './reports/ProductionRecordReport';
import BatchSummaryReport from './reports/BatchSummaryReport';
import MasterLotReport from './reports/MasterLotReport';
import LotDetailedReport from './reports/LotDetailedReport';
import MonitoringReport from './reports/MonitoringReport';
import MainCard from '@/components/MainCard';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';

const { Title } = Typography;

function ModernReport() {
  const [recipe, set_recipe] = useState('');
  const [batch, set_batch] = useState('');
  const [batch_type, set_batch_type] = useState('');
  const [downlaodPdf, setDownloadPdf] = useState([]);
  const [report_type, set_report_type] = useState('');
  const [fromDate, setFromDate] = useState(false);
  const [toDate, setToDate] = useState(false);
  const [batch_data, set_batch_data] = useState([]);
  const [recipe_data, set_recipe_data] = useState([]);
  const selectedButton = useSelector((state) => state.testProdView);
  // recipe_type boolean: true (Production) / false (Test).
  const recipeTypeFlag = selectedButton === 'production';
  const [product_list, set_product_list] = useState([]);
  const [product, set_product] = useState();
  const [tab, setTab] = useState('1');
  const [is_parent_batch, set_is_parent_batch] = useState(false);
  const [is_lot, set_is_lot] = useState(false);
  const selectedModule = useSelector((state) => state.moduleView);
  const [base64Data, set_base64Data] = useState();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [process_value, set_process_value] = useState('');
  const [final_report, set_final_report] = useState([]);
  const [report, set_report] = useState([]);
  const { t } = useTranslation();
  const { isDark } = useTheme();

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const pageBgColor = isDark ? defaultColors.palette.gray[900] : '#f9fafb';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const labelColor = isDark ? defaultColors.palette.gray[300] : '#374151';
  const tabBarBgColor = isDark ? defaultColors.palette.gray[700] : '#f9fafb';
  const loadingOverlayBg = isDark ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.9)';
  const stepActiveBg = isDark ? 'rgba(34, 197, 94, 0.2)' : '#dcfce7';
  const stepActiveText = isDark ? '#4ade80' : '#15803d';
  const stepInactiveBg = isDark ? defaultColors.palette.gray[700] : '#f3f4f6';
  const stepInactiveText = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const stepDotActive = isDark ? '#22c55e' : '#22c55e';
  const stepDotInactive = isDark ? defaultColors.palette.gray[500] : '#9ca3af';
  const stepLineBg = isDark ? defaultColors.palette.gray[600] : '#d1d5db';
  const formBgColor = isDark
    ? defaultColors.palette.gray[750] || defaultColors.palette.gray[700]
    : '#f8faff';

  // API Functions
  const get_data = async (value) => {
    try {
      await bp_get_batch_number_by_recipe(success_bp_get_batch_process_active, {
        recipe_id: recipe,
        is_completed: value && value?.trim()?.toLowerCase() === 'completed' ? true : false,
        recipe_type: recipeTypeFlag,
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const success_bp_get_batch_process_active = (res) => {
    set_batch_data(res?.data?.data);
  };

  const success_bp_product_data = (res) => {
    set_product_list(res?.data?.data);
  };

  const get_recipe = (product) => {
    try {
      get_recipe_by_product_id(success_recipe_by_product, {
        product_id: product,
        recipe_type: recipeTypeFlag,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const success_recipe_by_product = (res) => {
    if (res?.data?.status === 'success') {
      set_recipe_data(res?.data?.data);
    } else {
      set_recipe_data([]);
    }
  };

  const get_report = async (batch_value, type, start, end) => {
    try {
      const batchReq = { batch_number: batch_value, recipe_type: recipeTypeFlag };

      // Conditionally add start_date and end_date
      if (start && end) {
        batchReq.start_date = start;
        batchReq.end_date = end;
      }
      if (fromDate && toDate) {
        batchReq.start_date = fromDate;
        batchReq.end_date = toDate;
      }
      await bp_get_batch_report_view(success_bp_get_batch_report_view, batchReq);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const success_bp_get_batch_report_view = (res) => {
    if (res?.data?.status === 'success') {
      set_report(res?.data?.data || []);
    } else {
      toast.warning(res?.data?.message);
      set_report([]);
    }
  };

  const get_product_list = async () => {
    try {
      await get_product_list_both({ module_type: selectedModule, recipe_type: recipeTypeFlag }, success_bp_product_data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Effects
  useEffect(() => {
    get_product_list();
    set_product();
    set_recipe('');
    set_report([]);
    set_final_report([]);
    set_batch('');
    set_batch_type('');
    set_process_value('');
    set_batch_data([]);
    set_base64Data();
  }, [selectedButton, selectedModule]);

  // Mock data for demonstration - remove these when using real API
  const mockProductList = product_list || [];
  const mockRecipeData = recipe_data || [];
  const mockBatchData = batch_data || [];

  // Custom Select Component
  const CustomSelect = ({
    label,
    value,
    onChange,
    options,
    placeholder,
    disabled = false,
    icon: Icon,
    required = false,
  }) => {
    return (
      <div className="flex flex-col space-y-2">
        {/* Label */}
        <label
          className="text-sm font-semibold flex items-center gap-1"
          style={{ color: labelColor }}
        >
          {Icon && <Icon className="w-4 h-4" />}
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>

        {/* Ant Design Select */}
        <Select
          showSearch
          value={value}
          size="large"
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
          suffixIcon={<ChevronDown className="w-4 h-4" />}
          className="w-full"
        >
          {options.length === 0 ? (
            <Select.Option disabled>No options available</Select.Option>
          ) : (
            options.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))
          )}
        </Select>
      </div>
    );
  };

  // Tab Component
  const TabButton = ({ active, onClick, children, icon: Icon }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-6 py-3 font-medium rounded-lg transition-all duration-200 ${
        active ? 'text-white shadow-lg' : ''
      }`}
      style={
        active
          ? {
              backgroundColor: isDark ? '#0F172A' : defaultColors.primary,
              borderColor: isDark ? '#0F172A' : defaultColors.primary,
            }
          : {
              backgroundColor: cardBgColor,
              border: `1px solid ${borderColor}`,
              color: secondaryTextColor,
            }
      }
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );

  // Report types that render via their own dedicated, self-fetching component
  // (see reportComponentMap below) — handleDownload must NOT also fetch them,
  // otherwise the API would be called twice.
  const COMPONENT_REPORT_TYPES = [
    'Detailed Report',
    'Detailed Reports',
    'Production Record',
    'Batch Summary Report',
    'MasterLot Detailed Report',
    'Lot Detailed Report',
    'Monitoring Report',
  ];

  const handleDownload = async (batch, type) => {
    // Dedicated-component reports fetch themselves; just clear the PDF preview.
    if (COMPONENT_REPORT_TYPES.includes(type)) {
      set_base64Data('');
      return;
    }
    setLoading(true);
    try {
      {
        // Fallback for any report type not handled by a dedicated component.
        await get_new_pidilite_pdf_report(batch, (res) => {
          console.log(res);
          if (res?.data?.status === 'success') {
            set_base64Data(`data:application/pdf;base64,${res?.data?.data}`);
            setDownloadPdf(res?.data?.data);
          } else {
            toast?.warning(res?.data?.message);
            setDownloadPdf('');
            set_base64Data('');
          }
          setLoading(false);
        });
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      setLoading(false);
    }
  };

  const handleFileDownload = () => {
    setDownloading(true);
    try {
      if (tab === '2') {
        get_batch_report_pdf(batch, (res) => {
          base64ToFile(res?.data?.data, `${batch}_report`);
          setDownloading(false);
        });
      } else {
        base64ToFile(downlaodPdf, `${batch}_report`);
        setDownloading(false);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setDownloading(false);
    }
  };

  const productOptions = mockProductList.map((item) => ({
    value: item.product_id,
    label: item.product_name,
  }));

  const recipeOptions = mockRecipeData.map((item) => ({
    value: item.recipe_id,
    label: item.recipe_name,
    version_number: item.version_number,
  }));

  const batchOptions = mockBatchData.map((item) => ({
    value: item.batch_number,
    label: item.batch_number,
  }));

  const reportTypeOptions = is_parent_batch
    ? [{ value: 'MasterLot Detailed Report', label: 'MasterLot Detailed Report' }]
    : [
        ...(batch_type === 'completed'
          ? [
              { value: 'Detailed Report', label: 'Detailed Report' },
              { value: 'Monitoring Report', label: 'Monitoring Report' },
              { value: 'Production Record', label: 'Production Record' },
            ]
          : []),
        ...(batch_type === 'running'
          ? [{ value: 'Detailed Reports', label: 'Detailed Reports' }]
          : []),
        { value: 'Batch Summary Report', label: 'Batch Summary Report' },
        // {
        //   value: "Reaction Monitoring Report",
        //   label: "Reaction Monitoring Report",
        // },
        { value: 'Lot Detailed Report', label: 'Lot Detailed Report' },
      ];

  // Each report type maps to its own dedicated component file (under ./reports).
  // These components self-fetch from their API and render their own preview +
  // print, so Report.js just picks the right one by report_type.
  const reportComponentMap = {
    'Detailed Report': DetailedReport,
    'Detailed Reports': RunningReport, // running batch
    'Production Record': ProductionRecordReport,
    'Batch Summary Report': BatchSummaryReport,
    'MasterLot Detailed Report': MasterLotReport,
    'Lot Detailed Report': LotDetailedReport,
    'Monitoring Report': MonitoringReport,
  };
  const ReportComp = batch && report_type ? reportComponentMap[report_type] : null;
  const reportComponent = ReportComp ? <ReportComp batchNumber={batch} /> : null;

  return (
    <div className="p-2.5 pt-5" style={{ backgroundColor: pageBgColor }}>
      <Card
        className="border border-gray-300 shadow-md"
        bordered={false}
        style={{ backgroundColor: cardBgColor, minHeight: 'calc(100vh - 120px)' }}
        title={
          <div className="flex items-center gap-2">
            <Icons.File className="text-blue-600" size={24} />
            <Title
              level={4}
              style={{
                color: textColor,
                margin: 0,
                padding: 0,
              }}
            >
              {t('Report Details')}
            </Title>
          </div>
        }
      >
        {/* Filters */}
        <div
          className="rounded-xl border mb-5"
          style={{
            backgroundColor: formBgColor,
            borderColor: '#3b82f6',
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            padding: '20px 24px 12px',
          }}
        >
          {/* Form header */}
          <div className="flex items-center gap-2 mb-4">
            <div
              className="p-1.5 rounded-md"
              style={{ backgroundColor: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff' }}
            >
              <Icons.FileText className="w-4 h-4" style={{ color: '#3b82f6' }} />
            </div>
            <span className="text-sm font-semibold" style={{ color: '#3b82f6' }}>
              {t('Report Filters')}
            </span>
          </div>

          <Form layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={5}>
                <Form.Item
                  label={
                    <span
                      className="font-medium"
                      style={{ color: textColor }}
                    >
                      Product
                    </span>
                  }
                >
                  <Select
                    size="large"
                    showSearch
                    allowClear
                    value={product}
                    onChange={(value) => {
                      set_product(value);
                      set_recipe('');
                      set_batch('');
                      set_report_type('');
                      set_batch_type('');
                      set_base64Data('');
                      set_report([]);
                      set_final_report([]);
                      set_process_value('');
                      set_batch_data([]);
                      get_recipe(value);
                    }}
                    placeholder="Select Product"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                  >
                    {productOptions.map((o) => (
                      <Select.Option key={o.value} value={o.value}>
                        {o.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={5}>
                <Form.Item
                  label={
                    <span
                      className="font-medium"
                      style={{ color: textColor }}
                    >
                      MBMR
                    </span>
                  }
                >
                  <Select
                    size="large"
                    showSearch
                    allowClear
                    value={recipe}
                    disabled={!product}
                    onChange={(value) => {
                      set_recipe(value);
                      set_batch('');
                      set_batch_type('');
                      set_report_type('');
                      set_base64Data('');
                      set_report([]);
                      set_final_report([]);
                      set_process_value('');
                      set_batch_data([]);
                    }}
                    placeholder="Select MBMR"
                    filterOption={(input, option) =>
                      String(option?.['data-label'] ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                  >
                    {recipeOptions.map((o) => (
                      <Select.Option key={o.value} value={o.value} data-label={o.label}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {o.label}
                          {o.version_number ? (
                            <Tag color="blue" style={{ marginInlineEnd: 0, lineHeight: '18px' }}>
                              v{o.version_number}
                            </Tag>
                          ) : null}
                        </span>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={4}>
                <Form.Item
                  label={
                    <span
                      className="font-medium"
                      style={{ color: textColor }}
                    >
                      BMR Status
                    </span>
                  }
                >
                  <Select
                    size="large"
                    allowClear
                    value={batch_type}
                    disabled={!recipe}
                    onChange={(value) => {
                      set_batch_type(value);
                      set_report_type('');
                      set_batch('');
                      const parent = mockBatchData?.find((data) => data?.batch_number === value);
                      if (parent && parent?.is_parent) {
                        set_is_parent_batch(parent?.is_parent);
                      } else {
                        set_is_parent_batch(false);
                        parent && parent?.is_lot ? set_is_lot(parent?.is_lot) : set_is_lot(false);
                      }
                      get_data(value);
                    }}
                    placeholder="Select Status"
                    style={{ width: '100%' }}
                    options={[
                      { label: 'Running', value: 'running' },
                      { label: 'Completed', value: 'completed' },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={5}>
                <Form.Item
                  label={
                    <span
                      className="font-medium"
                      style={{ color: textColor }}
                    >
                      Batch
                    </span>
                  }
                >
                  <Select
                    size="large"
                    showSearch
                    allowClear
                    value={batch}
                    disabled={!batch_type}
                    onChange={(value) => {
                      set_batch(value);
                      set_report_type('');
                      const parent = mockBatchData?.find((data) => data?.batch_number === value);
                      if (parent && parent?.is_parent) {
                        set_is_parent_batch(parent?.is_parent);
                      } else {
                        set_is_parent_batch(false);
                        parent && parent?.is_lot ? set_is_lot(parent?.is_lot) : set_is_lot(false);
                      }
                    }}
                    placeholder="Select Batch"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children?.toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                  >
                    {batchOptions.map((o) => (
                      <Select.Option key={o.value} value={o.value}>
                        {o.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={5}>
                <Form.Item
                  label={
                    <span
                      className="font-medium"
                      style={{ color: textColor }}
                    >
                      Report Type
                    </span>
                  }
                >
                  <Select
                    size="large"
                    allowClear
                    value={report_type}
                    disabled={!batch}
                    onChange={(value) => {
                      set_report_type(value);
                      handleDownload(batch, value);
                    }}
                    placeholder="Select Type"
                    style={{ width: '100%' }}
                    options={reportTypeOptions}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>

        {/* Tabs */}
        <div
          className="rounded-xl shadow-sm overflow-hidden mt-6"
          style={{
            backgroundColor: isDark ? defaultColors.palette.gray[700] : '#f9fafb',
            border: `1px solid ${borderColor}`,
          }}
        >
          <div
            className="flex p-2 gap-2"
            style={{ borderBottom: `1px solid ${borderColor}`, backgroundColor: tabBarBgColor }}
          >
            <TabButton active={tab === '1'} onClick={() => setTab('1')} icon={FileText}>
              Overall Report
            </TabButton>
            {/* <TabButton
            active={tab === "2"}
            onClick={() => setTab("2")}
            icon={FileBarChart}
          >
            Individual Report
          </TabButton> */}
          </div>

          {/* Tab Content — no left/right padding so the report uses the full width. */}
          <div className="py-6 px-0">
            {tab === '1' && (
              <div
                className="relative h-[800px] rounded-xl shadow-inner overflow-hidden"
                style={{
                  background: isDark
                    ? defaultColors.palette.gray[700]
                    : 'linear-gradient(to bottom right, #f3f4f6, #e2e8f0)',
                  border: `1px solid ${borderColor}`,
                }}
              >
                {/* Loading Overlay */}
                {loading && (
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center backdrop-blur-sm z-10 rounded-xl"
                    style={{ backgroundColor: loadingOverlayBg }}
                  >
                    <div className="relative">
                      {/* Animated Loader */}
                      <div className="w-16 h-16 relative">
                        <div
                          className="absolute inset-0 border-4 rounded-full"
                          style={{
                            borderColor: isDark ? defaultColors.palette.steel[700] : '#dbeafe',
                          }}
                        ></div>
                        <div
                          className="absolute inset-0 border-4 border-transparent rounded-full animate-spin"
                          style={{
                            borderTopColor: isDark ? defaultColors.palette.steel[400] : '#2563eb',
                          }}
                        ></div>
                      </div>

                      {/* Loading Text */}
                      <div className="mt-4 text-center">
                        <p className="font-semibold" style={{ color: textColor }}>
                          Loading PDF...
                        </p>
                        <p className="text-sm mt-1" style={{ color: secondaryTextColor }}>
                          Generating your report
                        </p>
                      </div>

                      {/* Progress Dots */}
                      <div className="flex items-center justify-center gap-1 mt-3">
                        <div
                          className="w-2 h-2 rounded-full animate-pulse"
                          style={{
                            backgroundColor: isDark ? defaultColors.palette.steel[400] : '#2563eb',
                          }}
                        ></div>
                        <div
                          className="w-2 h-2 rounded-full animate-pulse"
                          style={{
                            animationDelay: '0.2s',
                            backgroundColor: isDark ? defaultColors.palette.steel[400] : '#2563eb',
                          }}
                        ></div>
                        <div
                          className="w-2 h-2 rounded-full animate-pulse"
                          style={{
                            animationDelay: '0.4s',
                            backgroundColor: isDark ? defaultColors.palette.steel[400] : '#2563eb',
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Each report type renders its own dedicated component file. */}
                {reportComponent ? (
                  <div className="overflow-auto" style={{ maxHeight: '82vh' }}>
                    {reportComponent}
                  </div>
                ) : /* PDF Viewer (fallback) or No Data */
                base64Data ? (
                  <div className="h-[780px] p-4">
                    <iframe
                      title="PDF Viewer"
                      src={base64Data}
                      className="w-full h-full border-0 rounded-lg"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center p-8">
                    {/* Animated Document Icon */}
                    <div className="relative mb-8">
                      <div
                        className="w-24 h-32 rounded-xl shadow-xl relative transform hover:scale-105 transition-transform duration-300"
                        style={{
                          background: isDark
                            ? defaultColors.palette.gray[600]
                            : 'linear-gradient(to bottom, #e5e7eb, #d1d5db)',
                        }}
                      >
                        {/* Document fold */}
                        <div
                          className="absolute top-0 right-0 w-6 h-6 transform rotate-45 translate-x-3 -translate-y-3 shadow-sm"
                          style={{
                            backgroundColor: isDark ? defaultColors.palette.gray[500] : '#f3f4f6',
                          }}
                        ></div>

                        {/* Content lines */}
                        <div className="absolute top-6 left-4 right-4 space-y-2">
                          <div
                            className="h-2 rounded opacity-50"
                            style={{
                              backgroundColor: isDark ? defaultColors.palette.gray[400] : '#9ca3af',
                            }}
                          ></div>
                          <div
                            className="h-2 rounded opacity-30 w-3/4"
                            style={{
                              backgroundColor: isDark ? defaultColors.palette.gray[400] : '#9ca3af',
                            }}
                          ></div>
                          <div
                            className="h-2 rounded opacity-30 w-1/2"
                            style={{
                              backgroundColor: isDark ? defaultColors.palette.gray[400] : '#9ca3af',
                            }}
                          ></div>
                          <div
                            className="h-2 rounded opacity-20 w-2/3"
                            style={{
                              backgroundColor: isDark ? defaultColors.palette.gray[400] : '#9ca3af',
                            }}
                          ></div>
                        </div>

                        {/* Floating elements */}
                        <div
                          className="absolute -top-2 -left-2 w-3 h-3 rounded-full animate-ping"
                          style={{
                            backgroundColor: isDark ? defaultColors.palette.steel[400] : '#60a5fa',
                          }}
                        ></div>
                        <div
                          className="absolute -bottom-2 -right-2 w-4 h-4 rounded-full animate-pulse"
                          style={{
                            backgroundColor: isDark ? defaultColors.palette.teal[400] : '#a78bfa',
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-2xl font-bold" style={{ color: textColor }}>
                        Select Report Parameters
                      </h3>
                      <p className="max-w-lg leading-relaxed" style={{ color: secondaryTextColor }}>
                        Choose your product, MBMR, batch, and report type from the filters above
                        to generate and view your detailed batch report.
                      </p>
                    </div>

                    {/* Steps Indicator */}
                    <div className="mt-8 flex items-center justify-center space-x-4 text-sm">
                      <div
                        className="flex items-center gap-2 px-3 py-2 rounded-full"
                        style={{
                          backgroundColor: product ? stepActiveBg : stepInactiveBg,
                          color: product ? stepActiveText : stepInactiveText,
                        }}
                      >
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: product ? stepDotActive : stepDotInactive }}
                        ></div>
                        Product
                      </div>
                      <div className="w-4 h-0.5" style={{ backgroundColor: stepLineBg }}></div>
                      <div
                        className="flex items-center gap-2 px-3 py-2 rounded-full"
                        style={{
                          backgroundColor: recipe ? stepActiveBg : stepInactiveBg,
                          color: recipe ? stepActiveText : stepInactiveText,
                        }}
                      >
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: recipe ? stepDotActive : stepDotInactive }}
                        ></div>
                        Recipe
                      </div>
                      <div className="w-4 h-0.5" style={{ backgroundColor: stepLineBg }}></div>
                      <div
                        className="flex items-center gap-2 px-3 py-2 rounded-full"
                        style={{
                          backgroundColor: batch ? stepActiveBg : stepInactiveBg,
                          color: batch ? stepActiveText : stepInactiveText,
                        }}
                      >
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: batch ? stepDotActive : stepDotInactive }}
                        ></div>
                        Batch
                      </div>
                      <div className="w-4 h-0.5" style={{ backgroundColor: stepLineBg }}></div>
                      <div
                        className="flex items-center gap-2 px-3 py-2 rounded-full"
                        style={{
                          backgroundColor: report_type ? stepActiveBg : stepInactiveBg,
                          color: report_type ? stepActiveText : stepInactiveText,
                        }}
                      >
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: report_type ? stepDotActive : stepDotInactive }}
                        ></div>
                        Report
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === '2' && (
              <div className="space-y-6">
                {(final_report?.length > 0 && final_report[0]?.operations?.length > 0) ||
                (report?.length > 0 && report[0]?.operations?.length > 0) ? (
                  <ReportSkeleton report={final_report?.length === 0 ? report : final_report} />
                ) : (
                  <div
                    className="rounded-xl p-8"
                    style={{
                      background: isDark
                        ? defaultColors.palette.gray[700]
                        : 'linear-gradient(to bottom right, #eff6ff, #eef2ff)',
                      border: `1px solid ${isDark ? defaultColors.palette.gray[600] : '#dbeafe'}`,
                    }}
                  >
                    <div className="text-center">
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{
                          background: isDark
                            ? `linear-gradient(to bottom right, ${defaultColors.palette.steel[600]}, ${defaultColors.palette.steel[700]})`
                            : 'linear-gradient(to bottom right, #3b82f6, #4f46e5)',
                        }}
                      >
                        <FileBarChart className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2" style={{ color: textColor }}>
                        Individual Report View
                      </h3>
                      <p style={{ color: secondaryTextColor }}>
                        {!batch
                          ? 'Select a batch to view detailed breakdown of individual operations and processes'
                          : 'Loading individual report data...'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ModernReport;
